import { action, computed, observable, makeObservable } from 'mobx';

import {
    ServiceCategory,
    ServiceData,
    ServiceQuery,
    CategorySpecialtyResponse,
} from 'chaika-frontend-types/frontend-cms';
import { Optional } from 'chaika-common-types';

import { SearchList } from '@store/abstract/SearchList';
import { ServiceList } from '../models/ServiceList';
import { TagList } from '../models/TagList';
import { ServiceRisksList } from '../models/ServiceRisksList';

export class ServiceStore {
    public static readonly Name = 'serviceStore';

    @observable public serviceList: ServiceList = new ServiceList();
    @observable public searchList = new SearchList<ServiceData, ServiceData, ServiceQuery, string>(ServiceList);
    @observable public tagList = new TagList();
    @observable public serviceRisksList = new ServiceRisksList();
    @observable private categorySpecialties = new Map<ServiceCategory, string[]>();

    constructor() {
        makeObservable(this);
    }

    @computed
    public get searchParams(): ServiceQuery {
        return {
            ...this.searchList.searchParams,
        };
    }

    @computed
    public get servicesIds(): string[] {
        return this.serviceList.ids;
    }

    @computed
    public get searchServicesIds(): string[] {
        return this.searchList.searchParamsSet ? this.searchList.ids : this.serviceList.ids;
    }

    @computed
    public get categorySpecialtiesLoaded(): boolean {
        return this.categorySpecialties.size > 0;
    }

    @action
    public setCategorySpecialties({ categorySpecialties }: CategorySpecialtyResponse): void {
        Object.keys(categorySpecialties).forEach(category => {
            this.categorySpecialties.set(category as ServiceCategory, categorySpecialties[category]);
        });
    }

    public getCategorySpecialties(category: ServiceCategory | string): string[] {
        return this.categorySpecialties.get(category as ServiceCategory) ?? [];
    }

    public isServiceLaboratory(serviceId: string): boolean {
        const service = this.getService(serviceId);
        return service?.category === ServiceCategory.Laboratory;
    }

    public isComplexService(serviceId: string): boolean {
        const service = this.getService(serviceId);
        return !!service?.isComplex;
    }

    public getServices(serviceIds: string[]): Optional<ServiceData>[] {
        return serviceIds.map(id => this.getService(id));
    }

    public getService(serviceId: string): Optional<ServiceData> {
        return this.serviceList.get(serviceId) || this.searchList.get(serviceId);
    }

    public getServicesValues(): ServiceData[] {
        return this.serviceList.values;
    }

    public getSearchServicesValues(): ServiceData[] {
        return this.searchList.searchParamsSet ? this.searchList.values : this.serviceList.values;
    }

    public getListWithCount(): { services: ServiceData[]; count: number } {
        const list = this.searchList.searchParamsSet ? this.searchList : this.serviceList;

        return {
            services: list.values,
            count: list.count,
        };
    }

    public getFilteredListWithCount(): { services: ServiceData[]; count: number } {
        const list = this.searchList.searchParamsSet ? this.searchList : this.serviceList;
        const filterParams = this.serviceList.getFilterParams();

        return {
            services: list.getFilteredValues(filterParams),
            count: list.count,
        };
    }

    @action
    public resetQuery(): void {
        this.searchList.reset();
    }
}

export const serviceStore = new ServiceStore();
